import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';

// ── DTOs ────────────────────────────────────────────────────────────

export interface MdoItemDTO {
  material_id: string;
  material_nombre: string;
  precio_unitario: number;
}

interface MaestroRateDTO {
  recipe_id: string;
  recipe_nombre: string;
  precio_unitario: number;
  unidad: string;
  mdo_items: MdoItemDTO[];
}

export interface PlanillaMaestroDTO {
  maestro_id: string;
  nombre: string;
  cedula: string | null;
  rates: MaestroRateDTO[];
}

export interface PlanillaListItemDTO {
  id: string;
  project_id: string;
  project_nombre?: string;
  numero: number;
  fecha_desde: string;
  fecha_hasta: string;
  estado: string;
  total_bruto: number;
  total_neto: number;
  created_by: { id: string; nombre: string };
  created_at: string;
  updated_at: string;
}

export interface PlanillaDetailItemDTO {
  id: string;
  maestro_id: string;
  maestro_nombre: string;
  recipe_id: string;
  recipe_nombre: string;
  mdo_material_id: string | null;
  mdo_material_nombre: string | null;
  cabecera_id: string | null;
  cabecera_nombre: string | null;
  cantidad: number;
  unidad: string;
  precio_unitario: number;
  subtotal: number;
  notas: string | null;
}

export interface ExtraDTO {
  id: string;
  maestro_id: string;
  categoria: string;
  recipe_id: string | null;
  cabecera_id: string | null;
  cabecera_nombre: string | null;
  descripcion: string;
  cantidad: number | null;
  monto: number;
  fotos: { id: string; s3_key: string; filename: string }[];
  created_at: string;
}

export interface DescuentoDTO {
  id: string;
  maestro_id: string;
  tipo: string;
  cabecera_id: string | null;
  cabecera_nombre: string | null;
  descripcion: string;
  monto: number;
  created_at: string;
}

export interface CommentDTO {
  id: string;
  mensaje: string;
  created_by: { id: string; nombre: string };
  created_at: string;
}

export interface FotoDTO {
  id: string;
  s3_key: string;
  filename: string;
  mime_type: string;
  created_at: string;
}

export interface PlanillaDetailDTO {
  id: string;
  project_id: string;
  project_nombre: string;
  ot_id: string;
  numero: number;
  fecha_desde: string;
  fecha_hasta: string;
  estado: string;
  comentarios: string | null;
  details: PlanillaDetailItemDTO[];
  extras: ExtraDTO[];
  descuentos: DescuentoDTO[];
  fotos: FotoDTO[];
  comments: CommentDTO[];
  maestros: PlanillaMaestroDTO[];
  totals: {
    total_rubros: number;
    total_extras: number;
    total_descuentos: number;
    total_neto: number;
  };
  created_by: { id: string; nombre: string };
  created_at: string;
  updated_at: string;
}

interface PlanillasResponse {
  data: PlanillaListItemDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ── Query key factory ───────────────────────────────────────────────

const planillaKeys = {
  all: queryKeys.planillas.all,
  list: (projectId: string, filters?: Record<string, unknown>) =>
    [...queryKeys.planillas.all, 'list', projectId, filters] as const,
  detail: (id: string) => queryKeys.planillas.detail(id),
};

// ── Global List (cross-project) ─────────────────────────────────────

export function useAllPlanillas(filters?: { estado?: string; projectId?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...queryKeys.planillas.all, 'all', filters] as const,
    queryFn: async () => {
      const res = await api.get<PlanillasResponse>('/planillas', { params: filters });
      return res.data;
    },
  });
}

// ── List (project-scoped) ───────────────────────────────────────────

export function usePlanillas(projectId: string, filters?: { estado?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: planillaKeys.list(projectId, filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await api.get<PlanillasResponse>(`/projects/${projectId}/planillas`, { params: filters });
      return res.data;
    },
    enabled: !!projectId,
  });
}

// ── Detail ──────────────────────────────────────────────────────────

export function usePlanillaDetail(planillaId: string) {
  return useQuery({
    queryKey: planillaKeys.detail(planillaId),
    queryFn: async () => {
      const res = await api.get<{ data: PlanillaDetailDTO }>(`/planillas/${planillaId}`);
      return res.data.data;
    },
    enabled: !!planillaId,
  });
}

// ── Create ──────────────────────────────────────────────────────────

export function useCreatePlanilla(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { fecha: string; comentarios?: string | null }) => {
      const res = await api.post<{ data: { id: string; numero: number; estado: string } }>(
        `/projects/${projectId}/planillas`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(projectId) });
    },
  });
}

// ── Upsert Details ──────────────────────────────────────────────────

// ── Create Extra ────────────────────────────────────────────────────

interface CreateExtraInput {
  maestroId: string;
  categoria: 'gypsum' | 'pintura' | 'varios' | 'varios_maestro';
  recipeId?: string | null;
  cabeceraId?: string | null;
  descripcion: string;
  cantidad?: number | null;
  monto: number;
}

export function useCreateExtra(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateExtraInput) => {
      const res = await api.post(`/planillas/${planillaId}/extras`, data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
    },
  });
}

export function useDeleteExtra(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (extraId: string) => {
      const res = await api.delete(`/extras/${extraId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
    },
  });
}

// ── Descuentos ──────────────────────────────────────────────────────

interface CreateDescuentoInput {
  maestroId: string | null;
  tipo: 'multa' | 'herramienta' | 'anticipo_maestro' | 'otro' | 'administrativo';
  cabeceraId?: string | null;
  descripcion: string;
  monto: number;
}

export function useCreateDescuento(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateDescuentoInput) => {
      const res = await api.post(`/planillas/${planillaId}/descuentos`, data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
    },
  });
}

export function useDeleteDescuento(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (descuentoId: string) => {
      const res = await api.delete(`/descuentos/${descuentoId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
    },
  });
}

// ── Comments ────────────────────────────────────────────────────────

export function useCreateComment(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mensaje: string) => {
      const res = await api.post(`/planillas/${planillaId}/comments`, { mensaje });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
    },
  });
}

// ── Submit (borrador → pendiente_administrativo) ────────────────────

export function useSubmitPlanilla(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<{ data: { id: string; estado: string; updated_at: string } }>(
        `/planillas/${planillaId}/submit`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
      queryClient.invalidateQueries({ queryKey: planillaKeys.all });
    },
  });
}

// ── Upsert Details ──────────────────────────────────────────────────

interface DetailInput {
  id?: string | null;
  maestroId: string;
  recipeId: string;
  mdoMaterialId?: string | null;
  cabeceraId?: string | null;
  cantidad: number;
  precioUnitario?: number | null;
  notas?: string | null;
}

export function useUpsertPlanillaDetails(planillaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (details: DetailInput[]) => {
      const res = await api.put(`/planillas/${planillaId}/details`, { details });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
      queryClient.invalidateQueries({ queryKey: planillaKeys.all });
    },
  });
}

// ── Transition hooks ───────────────────────────────────────────────

type TransitionResult = { data: { id: string; estado: string; updated_at: string } };

function useTransition(planillaId: string) {
  const queryClient = useQueryClient();
  return {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planillaKeys.detail(planillaId) });
      queryClient.invalidateQueries({ queryKey: planillaKeys.all });
    },
  };
}

export function usePassToAdmin(planillaId: string) {
  const { onSuccess } = useTransition(planillaId);
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<TransitionResult>(`/planillas/${planillaId}/pass-to-admin`);
      return res.data.data;
    },
    onSuccess,
  });
}

export function useReturnFromCompras(planillaId: string) {
  const { onSuccess } = useTransition(planillaId);
  return useMutation({
    mutationFn: async (comentario: string) => {
      const res = await api.post<TransitionResult>(`/planillas/${planillaId}/return-compras`, { comentario });
      return res.data.data;
    },
    onSuccess,
  });
}

export function useReturnToResidente(planillaId: string) {
  const { onSuccess } = useTransition(planillaId);
  return useMutation({
    mutationFn: async (comentario: string) => {
      const res = await api.post<TransitionResult>(`/planillas/${planillaId}/return`, { comentario });
      return res.data.data;
    },
    onSuccess,
  });
}

export function useSendToGerente(planillaId: string) {
  const { onSuccess } = useTransition(planillaId);
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<TransitionResult>(`/planillas/${planillaId}/send-to-gerente`);
      return res.data.data;
    },
    onSuccess,
  });
}

export function useApprovePlanilla(planillaId: string) {
  const { onSuccess } = useTransition(planillaId);
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<TransitionResult>(`/planillas/${planillaId}/approve`);
      return res.data.data;
    },
    onSuccess,
  });
}

export function useRejectPlanilla(planillaId: string) {
  const { onSuccess } = useTransition(planillaId);
  return useMutation({
    mutationFn: async (motivo: string) => {
      const res = await api.post<TransitionResult>(`/planillas/${planillaId}/reject`, { motivo });
      return res.data.data;
    },
    onSuccess,
  });
}

export function useUnlockPlanilla(planillaId: string) {
  const { onSuccess } = useTransition(planillaId);
  return useMutation({
    mutationFn: async (justificacion: string) => {
      const res = await api.post<TransitionResult>(`/planillas/${planillaId}/unlock`, { justificacion });
      return res.data.data;
    },
    onSuccess,
  });
}

// ── Planilla Progress ────────────────────────────────────────────

export interface PlanillaProgressItem {
  recipe_id: string;
  recipe_nombre: string;
  mdo_material_id: string | null;
  mdo_material_nombre: string | null;
  unidad: string;
  cantidad_ot: number;
  cantidad_registrada: number;
  porcentaje: number;
}

export function usePlanillaProgress(projectId: string) {
  return useQuery({
    queryKey: ['planilla-progress', projectId],
    queryFn: async () => {
      const res = await api.get<{ data: PlanillaProgressItem[] }>(`/projects/${projectId}/planilla-progress`);
      return res.data.data;
    },
    enabled: !!projectId,
  });
}

// ── Delete planilla (hard, borrador only) ──
export function useDeletePlanilla() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/planillas/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.planillas.all });
      toast('success', 'Planilla eliminada');
    },
  });
}
